/** Next / previous weapon (08 §3): the wheel and the bumpers walk this list, skipping what he has not got. Pure. */

import type { Gear, Inventory, WeaponId } from '../sim/types';

/** The order of the HUD's weapon panel and of the number keys. */
export const WEAPON_ORDER: readonly WeaponId[] = ['carbine', 'grenade', 'law', 'bomb', 'knife', 'pistol', 'smoke'];

/** Assumed when the caller does not say: gear weapons exist only when carried (04 §6). */
export const BASE_WEAPONS: readonly WeaponId[] = ['carbine', 'grenade', 'law', 'bomb', 'knife'];

/**
 * The weapon `steps` places along from `current` (negative = backwards), counting only weapons
 * in `available`. Null when that is no change: nothing else to select, or a full lap.
 */
export function cycleWeapon(
  current: WeaponId,
  steps: number,
  available: readonly WeaponId[] = BASE_WEAPONS,
): WeaponId | null {
  const dir = Math.sign(Math.trunc(steps));
  if (dir === 0) return null;
  const n = WEAPON_ORDER.length;
  let index = Math.max(0, WEAPON_ORDER.indexOf(current));
  for (let left = Math.abs(Math.trunc(steps)); left > 0; left--) {
    let moved = false;
    for (let hop = 1; hop <= n; hop++) {
      const at = (index + dir * hop + n * n) % n;
      const weapon = WEAPON_ORDER[at];
      if (weapon !== undefined && available.includes(weapon)) {
        index = at;
        moved = true;
        break;
      }
    }
    if (!moved) return null;
  }
  const picked = WEAPON_ORDER[index] ?? null;
  return picked === current ? null : picked;
}

/**
 * What the ranger could select right now - the sim's own rule (`hasAmmoFor`), for the caller of
 * `Input.command` to pass as `available`.
 */
export function availableWeapons(ranger: { inventory: Inventory; gear: Gear }): WeaponId[] {
  const { inventory: inv, gear } = ranger;
  const out: WeaponId[] = ['carbine'];
  if (inv.grenades > 0) out.push('grenade');
  if (inv.laws > 0) out.push('law');
  if (inv.bombs > 0) out.push('bomb');
  out.push('knife');
  if (gear.pistolRounds > 0) out.push('pistol');
  if (gear.smoke > 0) out.push('smoke');
  return out;
}
